import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DirectoryProfileReport, DirectoryProfileReportStatus } from "@vimos/api-client";
import { canEdit, useAuth } from "../../lib/auth";
import { supabase } from "../../lib/supabase";

type StatusFilter = DirectoryProfileReportStatus | "all";

const statusOptions: Array<{ value: DirectoryProfileReportStatus; label: string }> = [
  { value: "open", label: "Pendiente" },
  { value: "in_review", label: "En revisión" },
  { value: "resolved", label: "Resuelto" },
  { value: "dismissed", label: "Descartado" },
];

const topicLabels: Record<DirectoryProfileReport["topic"], string> = {
  contacto: "Contacto",
  ubicacion: "Ubicación",
  horario: "Horario",
  fotos: "Fotos",
  descripcion: "Descripción y oferta",
  otro: "Otro dato",
};

const statusStyles: Record<DirectoryProfileReportStatus, string> = {
  open: "bg-amber-50 text-amber-800 ring-amber-200",
  in_review: "bg-blue-50 text-blue-800 ring-blue-200",
  resolved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  dismissed: "bg-slate-100 text-slate-600 ring-slate-200",
};

const formatDate = (value: string) =>
  new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Lima",
  }).format(new Date(value));

async function fetchReports(): Promise<DirectoryProfileReport[]> {
  const { data, error } = await supabase
    .from("directory_profile_reports")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return data ?? [];
}

export function DirectoryProfileReportsPage() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const userCanEdit = canEdit(profile);
  const [filter, setFilter] = useState<StatusFilter>("open");
  const reportQuery = useQuery({
    queryKey: ["directory_profile_reports"],
    queryFn: fetchReports,
  });
  const updateReport = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: DirectoryProfileReportStatus }) => {
      const { error } = await supabase
        .from("directory_profile_reports")
        .update({ status } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["directory_profile_reports"] }),
  });

  const reports = useMemo(() => {
    const all = reportQuery.data ?? [];
    return filter === "all" ? all : all.filter((report) => report.status === filter);
  }, [filter, reportQuery.data]);

  return (
    <div className="space-y-5 pb-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-700">
            Calidad del directorio
          </p>
          <h1 className="font-display mt-1 text-2xl font-bold text-slate-950">Reportes de fichas</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Revisa los avisos de visitantes. Ningún reporte modifica la información pública por sí solo.
          </p>
        </div>
        <label className="text-sm font-semibold text-slate-700">
          Estado
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value as StatusFilter)}
            className="ml-2 h-10 rounded-lg border border-slate-200 bg-white px-3 font-medium"
          >
            <option value="open">Pendientes</option>
            <option value="in_review">En revisión</option>
            <option value="resolved">Resueltos</option>
            <option value="dismissed">Descartados</option>
            <option value="all">Todos los últimos 200</option>
          </select>
        </label>
      </header>

      {reportQuery.isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Cargando reportes…
        </div>
      ) : reportQuery.error ? (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800">
          No se pudieron cargar los reportes. Revisa el acceso del equipo y la migración de Supabase.
        </div>
      ) : reports.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <p className="font-semibold text-slate-800">No hay reportes en esta vista.</p>
          <p className="mt-1 text-sm text-slate-500">Los avisos nuevos aparecerán aquí para su revisión.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <article
              key={report.id}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-display text-lg font-bold text-slate-950">{report.store_name}</h2>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusStyles[report.status]}`}
                    >
                      {statusOptions.find((option) => option.value === report.status)?.label}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                      {topicLabels[report.topic]}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Enviado {formatDate(report.created_at)}</p>
                </div>
                <a
                  href={`${(import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined)?.replace(/\/+$/, "") ?? "https://vimosdigital.com"}/tienda/${encodeURIComponent(report.store_slug)}/`}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-sm font-semibold text-blue-700 hover:text-blue-900"
                >
                  Abrir ficha ↗
                </a>
              </div>

              <p className="mt-4 whitespace-pre-wrap break-words rounded-lg bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
                {report.details}
              </p>
              {report.reporter_email ? (
                <p className="mt-3 text-sm text-slate-600">
                  Contacto del visitante:{" "}
                  <a
                    className="font-medium text-blue-700 hover:underline"
                    href={`mailto:${report.reporter_email}`}
                  >
                    {report.reporter_email}
                  </a>
                </p>
              ) : (
                <p className="mt-3 text-xs text-slate-400">El visitante no dejó un correo de contacto.</p>
              )}

              {userCanEdit ? (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  {statusOptions
                    .filter((option) => option.value !== report.status)
                    .map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        disabled={updateReport.isPending}
                        onClick={() => updateReport.mutate({ id: report.id, status: option.value })}
                        className="min-h-9 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        {option.value === "in_review"
                          ? "Atender"
                          : option.value === "resolved"
                            ? "Marcar resuelto"
                            : option.value === "dismissed"
                              ? "Descartar"
                              : "Reabrir"}
                      </button>
                    ))}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
